import { createContext, useEffect, useContext, useCallback, useRef } from "react"
import { CurrenciesAllowedEnum } from "@tutellus/core-contracts-ifaces"
import { Toaster, ToasterHandle, ToastProps } from "@tutellus/tutellus-components"
import { useLocalStorage } from "../../hooks/useLocalStorage"
import {
  CURRENCY_STORAGE_KEY,
  LayoutContextInterface,
  LayoutProviderProps,
  THEME_MODE_STORAGE_KEY,
  ThemeMode,
} from "./types"

const initialState: LayoutContextInterface = {
  themeMode: ThemeMode.LIGHT,
  setThemeMode: () => {},
  currencyID: CurrenciesAllowedEnum.USD,
  setCurrencyID: () => {},
  addToast: () => {},
  removeToast: () => {},
  clearAllToasts: () => {},
}

const Context = createContext<LayoutContextInterface>(initialState)

export const LayoutContext = ({ children }: LayoutProviderProps) => {
  const [themeMode, setThemeMode] = useLocalStorage<ThemeMode>(THEME_MODE_STORAGE_KEY, ThemeMode.LIGHT)
  const [currencyID, setCurrencyID] = useLocalStorage<CurrenciesAllowedEnum>(CURRENCY_STORAGE_KEY, CurrenciesAllowedEnum.USD) // prettier-ignore

  const toasterRef = useRef<ToasterHandle>(null)

  const addToast = useCallback((toast: Omit<ToastProps, "id" | "isExiting" | "onClose"> & { duration?: number }) => toasterRef.current?.addToast({ ...toast, onClose: () => { } }), []) // prettier-ignore
  const removeToast = useCallback((id: string) => toasterRef.current?.removeToast(id), [])
  const clearAllToasts = useCallback(() => toasterRef.current?.clearAllToasts(), [])

  // Apply theme to root element
  useEffect(() => {
    const rootElement = document.getElementById("root")
    if (rootElement) {
      rootElement.classList.remove(ThemeMode.LIGHT, ThemeMode.DARK)
      rootElement.classList.add(themeMode)
    }
  }, [themeMode])

  return (
    <Context.Provider
      value={{
        themeMode,
        setThemeMode,
        currencyID,
        setCurrencyID,
        addToast,
        removeToast,
        clearAllToasts,
      }}
    >
      <Toaster ref={toasterRef} />
      {children}
    </Context.Provider>
  )
}

export const useLayout = function () {
  const context = useContext(Context)
  if (context === undefined) {
    throw new Error(`useLayout must be used within a LayoutContextProvider`)
  }
  return context
}

export { type LayoutContextInterface, type LayoutProviderProps, ThemeMode }
